import React, { useEffect, useRef, useState } from 'react';

import { Link } from 'react-router-dom';
import { useParivar } from '../../context/ParivarContext';
import './ParivarSelector.scss';

/**
 * Compact Parivar switcher for the navbar. Sits next to the profile /
 * notification cluster on desktop and inline in the expanded hamburger menu
 * on mobile. Lists the active Parivars served by the backend and persists the
 * visitor's choice (default: Sarkhej).
 */
const ParivarSelector = ({ mobile = false }) => {
  const { availableParivars, selectedParivar, ready, changeParivar } = useParivar();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Drop the menu if the selection changes underneath us (e.g. initial load).
  useEffect(() => {
    setOpen(false);
  }, [selectedParivar?.id]);

  const current = selectedParivar || { slug: 'sarkhej', name: 'Sarkhej' };
  const label = current.name || 'Sarkhej';
  const selectedId = selectedParivar?.id;

  return (
    <div className={`parivar-selector ${mobile ? 'mobile' : ''} ${open ? 'open' : ''}`} ref={boxRef}>
      <button
        type="button"
        className="parivar-selector-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={!ready}
        onClick={() => setOpen((o) => !o)}
        title="Select Parivar"
      >
        <i className="fas fa-map-marker-alt" aria-hidden="true" />
        <span className="parivar-selector-label">{label}</span>
        <i className="fas fa-chevron-down parivar-selector-chev" aria-hidden="true" />
      </button>

      {open && (
        <div className="parivar-selector-menu" role="listbox" aria-label="Select Parivar">
          <div className="parivar-selector-head">
            <span className="parivar-selector-head-om" aria-hidden="true">ॐ</span>
            <span className="parivar-selector-head-text">
              <strong>Our Parivars</strong>
              <small>Select your Temple</small>
            </span>
          </div>
          {availableParivars.length === 0 && (
            <span className="parivar-selector-empty">No Parivars available right now.</span>
          )}
          {availableParivars.map((p) => (
            <button
              type="button"
              role="option"
              aria-selected={p.id === selectedId}
              key={p.id}
              className={`parivar-selector-item${p.id === selectedId ? ' active' : ''}`}
              onClick={() => {
                changeParivar(p);
                setOpen(false);
              }}
            >
              <span className="parivar-selector-item-pin" aria-hidden="true">
                <i className="fas fa-map-marker-alt"></i>
              </span>
              <span className="parivar-selector-item-name">{p.name}</span>
              {p.id === selectedId && <i className="fas fa-check parivar-selector-item-check" aria-hidden="true" />}
            </button>
          ))}

          <div className="parivar-selector-divider" role="separator" aria-hidden="true" />
          <div className="parivar-selector-footer">
            <div className="parivar-selector-footer-glow" aria-hidden="true"></div>
            <Link
              to={`/parivar/${selectedParivar?.slug || 'sarkhej'}`}
              className="parivar-selector-details"
              onClick={() => setOpen(false)}
            >
              <i className="fas fa-temple parivar-selector-details-icon" aria-hidden="true"></i>
              <span>Parivar Details</span>
              <i className="fas fa-arrow-right parivar-selector-details-arrow" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParivarSelector;